/**
 * Benachrichtigungsarten und ihre Einstellungen (F-30). Jede Art ist einzeln
 * an- und abschaltbar, mit Vorlauf und/oder Uhrzeit. Gespeichert wird nur,
 * was vom Standard abweicht - neue Arten bekommen so automatisch ihren
 * Standard.
 */
import { rueckblickTagAus, STANDARD_RUECKBLICK_TAG } from '@/domain/review/faellig';

export type MeldungsArt =
  | 'termin' | 'deadline' | 'klausur' | 'meilenstein' | 'block'
  | 'ueberfaellig' | 'deadlineVorbei' | 'gewohnheit' | 'journalMorgen' | 'journalAbend'
  | 'bestaetigen' | 'reflexion' | 'sicherung'
  | 'tagesueberblick'
  | 'training' | 'trainingOffen'
  | 'trinken' | 'ernaehrungEintragen';

export type Gruppe = 'anstehend' | 'verpasst' | 'zusammenfassung';

export interface ArtInfo {
  art: MeldungsArt;
  gruppe: Gruppe;
  label: string;
  /** Erklaerung in den Einstellungen. */
  hinweis: string;
  /** Vorlauf: Einheit und Standard (Tage bzw. Minuten). */
  vorlauf?: { einheit: 'tage' | 'minuten'; standard: number };
  /** Uhrzeit 'HH:mm', zu der die Meldung kommt. */
  uhrzeit?: string;
  /** Standardmaessig aus - erst auf Wunsch (z. B. Ernaehrung, F-35 E). */
  standardAus?: boolean;
  /**
   * Meldet nur an bestimmten Tagen (Vorlauf, Tag selbst) - werktags bzw. an
   * freien Tagen gibt es deshalb kein "aus", nur eine andere Uhrzeit (F-41).
   * Sonst fiele z. B. die Erinnerung am Sonntag vor einer Montagsklausur weg.
   */
  einmalig?: boolean;
}

export const ARTEN: ArtInfo[] = [
  { art: 'termin', gruppe: 'anstehend', label: 'Termin beginnt bald', hinweis: 'Die Erinnerungen, die am Termin eingestellt sind.' },
  { art: 'deadline', gruppe: 'anstehend', label: 'Deadline naht', hinweis: 'Einmal mit Vorlauf, einmal am Tag selbst.', vorlauf: { einheit: 'tage', standard: 3 }, uhrzeit: '08:00', einmalig: true },
  { art: 'klausur', gruppe: 'anstehend', label: 'Klausur naht', hinweis: 'Mit Vorlauf und am Vortag.', vorlauf: { einheit: 'tage', standard: 7 }, uhrzeit: '08:00', einmalig: true },
  { art: 'meilenstein', gruppe: 'anstehend', label: 'Meilenstein naht', hinweis: 'Mit Vorlauf und am Tag selbst.', vorlauf: { einheit: 'tage', standard: 2 }, uhrzeit: '08:00', einmalig: true },
  { art: 'training', gruppe: 'anstehend', label: 'Training heute', hinweis: 'Die Einheiten des aktiven Plans (F-34), einmal am Morgen.', uhrzeit: '07:30' },
  { art: 'block', gruppe: 'anstehend', label: 'Eingeplanter Aufgabenblock beginnt', hinweis: 'Zeitblöcke aus Aufgaben.', vorlauf: { einheit: 'minuten', standard: 5 } },
  { art: 'ueberfaellig', gruppe: 'verpasst', label: 'Aufgaben überfällig', hinweis: 'Einmal täglich, gebündelt.', uhrzeit: '09:00' },
  { art: 'deadlineVorbei', gruppe: 'verpasst', label: 'Deadline überschritten', hinweis: 'Einmal täglich, gebündelt.', uhrzeit: '09:00' },
  { art: 'gewohnheit', gruppe: 'verpasst', label: 'Gewohnheiten noch offen', hinweis: 'Abends, wenn heute Vorgesehenes fehlt.', uhrzeit: '20:00' },
  { art: 'trainingOffen', gruppe: 'verpasst', label: 'Einheit noch nicht absolviert', hinweis: 'Abends, wenn eine geplante Einheit fehlt.', uhrzeit: '20:00' },
  { art: 'trinken', gruppe: 'verpasst', label: 'Noch wenig getrunken', hinweis: 'Wenn bis dahin weniger als die Hälfte des Trinkziels erreicht ist (Ernährung).', uhrzeit: '15:00', standardAus: true },
  { art: 'ernaehrungEintragen', gruppe: 'verpasst', label: 'Mahlzeiten noch nicht eingetragen', hinweis: 'Abends, wenn weniger Mahlzeiten erfasst sind, als für einen vollständigen Tag nötig.', uhrzeit: '20:30', standardAus: true },
  { art: 'journalMorgen', gruppe: 'verpasst', label: 'Morgenjournal fehlt', hinweis: 'Wenn es bis dahin nicht ausgefüllt ist.', uhrzeit: '10:00' },
  { art: 'journalAbend', gruppe: 'verpasst', label: 'Abendjournal fehlt', hinweis: 'Wenn es bis dahin nicht ausgefüllt ist.', uhrzeit: '21:30' },
  { art: 'bestaetigen', gruppe: 'verpasst', label: 'Termine noch nicht bestätigt', hinweis: 'Vergangene Termine der letzten 7 Tage (F-29).', uhrzeit: '21:00' },
  { art: 'reflexion', gruppe: 'verpasst', label: 'Wochen- oder Monatsreflexion steht aus', hinweis: 'Am Rückblick-Tag bzw. Monatsletzten und an den zwei Tagen danach.', uhrzeit: '18:00' },
  { art: 'sicherung', gruppe: 'verpasst', label: 'Datensicherung überfällig', hinweis: 'Wenn die letzte Sicherung so viele Tage her ist.', vorlauf: { einheit: 'tage', standard: 7 }, uhrzeit: '19:00' },
  { art: 'tagesueberblick', gruppe: 'zusammenfassung', label: 'Tagesüberblick am Morgen', hinweis: 'Termine, Fälliges und Wochenziele des Tages.', uhrzeit: '07:00' },
];

export const GRUPPEN: Record<Gruppe, string> = {
  anstehend: 'Anstehend',
  verpasst: 'Verpasst und überfällig',
  zusammenfassung: 'Zusammenfassung',
};

export interface ArtEinstellung {
  an: boolean;
  vorlauf?: number;
  /** Werktags `'HH:mm'`; `null` = werktags aus (F-41). */
  uhrzeit?: string | null;
  /** Wochenende, Feiertage, Ferien (F-41): fehlt = wie werktags, `null` = aus, sonst `'HH:mm'`. */
  uhrzeitFrei?: string | null;
}

/**
 * Uhrzeit einer Art an einem Tag - `null`: an diesem Tag keine Meldung.
 * Regeln und Oberflaeche lesen Uhrzeiten nur hierueber: ein `?? '08:00'`
 * machte aus "aus" (null) stillschweigend 8 Uhr.
 */
export function uhrzeitAm(w: ArtEinstellung, frei: boolean): string | null {
  if (!w.an) return null;
  const werktags = w.uhrzeit === null ? null : (w.uhrzeit ?? '08:00');
  return frei && w.uhrzeitFrei !== undefined ? w.uhrzeitFrei : werktags;
}

export interface Einstellungen {
  arten: Record<MeldungsArt, ArtEinstellung>;
  /** Ruhezeit 'HH:mm' - darin wird nichts gemeldet, es kommt gesammelt danach. */
  ruheVon: string;
  ruheBis: string;
  /** Push (F-31): neutrale Texte ohne Titel. */
  neutral: boolean;
  /**
   * Rueckblick-Tag (F-40), 0 = Sonntag ... 6 = Samstag: ab dann steht die
   * Wochenreflexion aus - in der Glocke und auf der Uebersicht.
   */
  rueckblickTag: number;
}

export const STANDARD: Einstellungen = {
  arten: Object.fromEntries(ARTEN.map((a) => [a.art, {
    an: !a.standardAus,
    ...(a.vorlauf ? { vorlauf: a.vorlauf.standard } : {}),
    ...(a.uhrzeit ? { uhrzeit: a.uhrzeit } : {}),
  }])) as Record<MeldungsArt, ArtEinstellung>,
  ruheVon: '22:00',
  ruheBis: '07:00',
  neutral: false,
  rueckblickTag: STANDARD_RUECKBLICK_TAG,
};

const UHRZEIT = /^([01]\d|2[0-3]):[0-5]\d$/;
const zeit = (v: unknown): string | undefined => (typeof v === 'string' && UHRZEIT.test(v) ? v : undefined);

/** Gespeicherte (evtl. alte oder kaputte) Werte mit dem Standard zusammenfuehren. */
export function einstellungenAus(roh: unknown): Einstellungen {
  const r = (roh && typeof roh === 'object' ? roh : {}) as Partial<Einstellungen>;
  const arten = { ...STANDARD.arten };
  for (const info of ARTEN) {
    const x = (r.arten as Record<string, Partial<ArtEinstellung>> | undefined)?.[info.art];
    if (!x) continue;
    const std = STANDARD.arten[info.art];
    arten[info.art] = {
      an: typeof x.an === 'boolean' ? x.an : std.an,
      ...(info.vorlauf ? { vorlauf: Number.isInteger(x.vorlauf) && x.vorlauf! >= 0 && x.vorlauf! <= 1440 ? x.vorlauf : std.vorlauf } : {}),
      ...(info.uhrzeit ? {
        uhrzeit: x.uhrzeit === null && !info.einmalig ? null : (zeit(x.uhrzeit) ?? std.uhrzeit),
        ...(x.uhrzeitFrei === null && !info.einmalig ? { uhrzeitFrei: null } : zeit(x.uhrzeitFrei) ? { uhrzeitFrei: zeit(x.uhrzeitFrei) } : {}),
      } : {}),
    };
  }
  return {
    arten,
    ruheVon: typeof r.ruheVon === 'string' && UHRZEIT.test(r.ruheVon) ? r.ruheVon : STANDARD.ruheVon,
    ruheBis: typeof r.ruheBis === 'string' && UHRZEIT.test(r.ruheBis) ? r.ruheBis : STANDARD.ruheBis,
    neutral: typeof r.neutral === 'boolean' ? r.neutral : STANDARD.neutral,
    rueckblickTag: rueckblickTagAus(r.rueckblickTag),
  };
}
